// src/components/DataLoaded.js
import React, { useState, useEffect } from "react";
import { Loader, Progress } from "@mantine/core";
import { Flex, Text } from "@mantine/core";
import { useTranslation } from "react-i18next";
import { useProgressiveLoading } from "../contexts/ThreadsContext";
import { formatDateRange } from "../utils/progressiveLoadingUtils";
import zIndex from "../designTokens/zIndex";
import GradientBox from "./patterns/color/GradientBox";
import colors from "../designTokens/colors.esm";

const DataLoader = ({ 
	msg, 
	isLoad, 
	subMessage = null,
	stage = null,
	progress = null,
	showElapsedTime = true,
	isLogin = false,
	isXL = false,
	isPublicRoute = false,
	useProgressiveMode = false // 새로운 prop: 점진적 로딩 모드 사용 여부
}) => {
	const { t } = useTranslation();
	const [elapsedTime, setElapsedTime] = useState(0);
	const { progressiveState } = useProgressiveLoading();

	// 점진적 로딩 메시지 생성
	const getProgressiveMessage = () => {
		if (!useProgressiveMode) return msg;

		const { stage, pageCount, threadCount } = progressiveState;
		
		switch (stage) {
			case 'connecting':
				return t("loading.connecting_threads");
			case 'loading-range':
				return t("loading.loading_threads_range");
			case 'page-loaded':
				return t("loading.processing_page");
			case 'saving':
				return t("loading.saving_chunk");
			case 'complete':
				return t("loading.loading_complete");
			default:
				return msg || t("loading.initializing");
		}
	};

	// 점진적 로딩 서브 메시지 생성 
	const getProgressiveSubMessage = () => {
		if (!useProgressiveMode) return subMessage;

		const { stage, dateRange, pageCount, threadCount, progress } = progressiveState;

		// 날짜 범위가 있는 경우 표시
		if (dateRange.start && dateRange.end) {
			const dateRangeText = formatDateRange(dateRange.start, dateRange.end);
			
			if (stage === 'loading-range' || stage === 'page-loaded') {
				if (pageCount > 0) {
					return t("loading.page_progress_with_range", 
						{ dateRange: dateRangeText, pageCount, threadCount }
					);
				}
				return t("loading.date_range_progress", { dateRange: dateRangeText });
			}
		}

		// 페이지/스레드 정보만 있는 경우
		if (pageCount > 0 || threadCount > 0) {
			return t("loading.threads_progress", { pageCount, threadCount });
		}

		return subMessage;
	};

	// 점진적 로딩 진행률 계산
	const getProgressiveProgress = () => {
		if (!useProgressiveMode) return progress;

		const { progress: progressValue } = progressiveState;
		
		if (progressValue > 0) {
			return {
				completed: progressValue,
				total: 100
			};
		}

		return progress;
	};

	// 경과 시간 추적
	useEffect(() => {
		if (!isLoad || !showElapsedTime) return;

		const startTime = Date.now();
		const timer = setInterval(() => {
			setElapsedTime(Date.now() - startTime);
		}, 1000);

		return () => {
			clearInterval(timer);
			setElapsedTime(0);
		};
	}, [isLoad, showElapsedTime]);

	if (!isLoad) return null;

	// 경과 시간 포맷팅
	const formatElapsedTime = (ms) => {
		const seconds = Math.floor(ms / 1000);
		if (seconds < 60) return `${seconds}${t("loading.seconds")}`;
		const minutes = Math.floor(seconds / 60);
		const remainingSeconds = seconds % 60;
		return `${minutes}${t("loading.minutes")} ${remainingSeconds}${t("loading.seconds")}`;
	};

	// Main 영역의 left offset 계산
	const getMainLeftOffset = () => {
		// SideMenu가 없는 경우 (로그인 안 했거나 공개 라우트): 정중앙
		if (!isLogin || isPublicRoute) return 0;
		// XL 브레이크포인트 이상: SideMenu가 좌측에 있지만 DataLoader는 중앙
		if (isXL) return 0;
		// XL 미만이고 로그인한 경우: SideMenu가 없으므로 정중앙
		return 0;
	};

	// 최종 표시할 데이터 계산
	const displayMessage = getProgressiveMessage();
	const displaySubMessage = getProgressiveSubMessage();
	const displayProgress = getProgressiveProgress();

	return (
		<GradientBox
			type="linear"
			palette={[colors.inkGray[9], colors.inkGray[7], colors.inkGray[9]]}
			angle={90}
			animated={true}
			animationDuration={4}
			sx={{
				position: "fixed",
				bottom: "24px",
				left: `calc(50% + ${getMainLeftOffset()}px)`,
				transform: "translateX(-50%)",
				borderRadius: "8px",
				zIndex: zIndex.dataLoader,
				minWidth: "320px",
				maxWidth: "420px",
				padding: 0,
				minHeight: "auto",
			}}
		>
			<Flex
				justify="center"
				align="center"
				direction="column"
				c="gray.1"
				p={24}
				gap={12}
				style={{
					textAlign: "center",
					background: "transparent",
					width: "100%",
				}}
			>
				<Loader type="dots" color="gray.1" size="lg" />
				<Text fz="xl" fw={600}>{displayMessage}</Text>

				{/* 진행률 바 */}
				{displayProgress && displayProgress.total > 0 && (
					<Progress
						value={(displayProgress.completed / displayProgress.total) * 100}
						size="md"
						radius="sm"
						color="white"
						style={{ width: "100%" }}
					/>
				)}

				{/* 서브 메시지 (날짜 범위, 진행 상황 등) */}
				{displaySubMessage && (
					<Text fz="md" c="gray.2" style={{ textAlign: "center" }}>
						{displaySubMessage}
					</Text>
				)}

				{/* 경과 시간 표시 */}
				{showElapsedTime && elapsedTime > 3000 && (
					<Text fz="md" c="gray.2">
						{t("loading.elapsed_time")} {formatElapsedTime(elapsedTime)}
					</Text>
				)}
			</Flex>
		</GradientBox>
	);
};

export default DataLoader;
